import React, { useState } from 'react';
import { LicenseService, License } from '../../firebase/services/licenseService';
import { UserService, UserProfile } from '../../firebase/services/userService';
import { AdminService } from '../../firebase/services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';

interface SupportToolsProps {
  licenses: License[];
  users: UserProfile[];
  onLicenseUpdate: () => void;
}

const LICENSE_TYPES = [
  { id: 'social-pro', name: 'Lava Social Pro', price: 29, maxSites: 1 },
  { id: 'layout-pro', name: 'Lava Layout Pro', price: 19, maxSites: 1 },
  { id: 'ecommerce-pro', name: 'Lava E-commerce Pro', price: 39, maxSites: 1 },
  { id: 'complete', name: 'Lava Complete', price: 79, maxSites: 5 }
];

export default function SupportTools({ licenses, users, onLicenseUpdate }: SupportToolsProps) {
  const { currentUser } = useAuth();
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedLicense, setSelectedLicense] = useState<string>('');
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // New license creation state
  const [showCreateLicense, setShowCreateLicense] = useState(false);
  const [newLicenseType, setNewLicenseType] = useState('');
  const [newLicensePrice, setNewLicensePrice] = useState(0);
  const [newLicenseDuration, setNewLicenseDuration] = useState(12); // months
  const [isFreeLicense, setIsFreeLicense] = useState(false);
  const [licenseReason, setLicenseReason] = useState('');

  const handleExtendLicense = async () => {
    if (!selectedLicense) {
      setMessage('Please select a license to extend');
      return;
    }

    setLoading(true);
    try {
      const license = licenses.find(l => l.id === selectedLicense);
      if (!license) {
        setMessage('License not found');
        return;
      }

      // Extend by 1 year
      const newExpiry = new Date(license.expiryDate.toDate());
      newExpiry.setFullYear(newExpiry.getFullYear() + 1);

      await LicenseService.updateLicense(selectedLicense, {
        expiryDate: newExpiry as any,
        status: 'active'
      });

      setMessage('License extended successfully!');
      onLicenseUpdate();
    } catch (error) {
      console.error('Error extending license:', error);
      setMessage('Error extending license');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSiteToLicense = async () => {
    if (!selectedLicense || !domain) {
      setMessage('Please select a license and enter a domain');
      return;
    }

    setLoading(true);
    try {
      await LicenseService.addSiteToLicense(selectedLicense, domain);
      setMessage('Site added to license successfully!');
      setDomain('');
      onLicenseUpdate();
    } catch (error) {
      console.error('Error adding site:', error);
      setMessage(`Error adding site: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateLicense = async () => {
    if (!selectedLicense) {
      setMessage('Please select a license to activate');
      return;
    }

    setLoading(true);
    try {
      await LicenseService.updateLicense(selectedLicense, {
        status: 'active'
      });

      setMessage('License activated successfully!');
      onLicenseUpdate();
    } catch (error) {
      console.error('Error activating license:', error);
      setMessage('Error activating license');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendLicense = async () => {
    if (!selectedLicense) {
      setMessage('Please select a license to suspend');
      return;
    }

    setLoading(true);
    try {
      await LicenseService.updateLicense(selectedLicense, {
        status: 'suspended'
      });

      setMessage('License suspended successfully!');
      onLicenseUpdate();
    } catch (error) {
      console.error('Error suspending license:', error);
      setMessage('Error suspending license');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewLicense = async () => {
    if (!selectedUser || !newLicenseType) {
      setMessage('Please select a user and license type');
      return;
    }

    setLoading(true);
    try {
      const licenseType = LICENSE_TYPES.find(lt => lt.id === newLicenseType);
      if (!licenseType) {
        setMessage('Invalid license type');
        return;
      }

      const now = new Date();
      const expiryDate = new Date(now);
      expiryDate.setMonth(expiryDate.getMonth() + newLicenseDuration);

      const newLicense: Omit<License, 'id'> = {
        userId: selectedUser,
        licenseType: newLicenseType as any,
        status: 'active',
        purchaseDate: Timestamp.now(),
        expiryDate: Timestamp.fromDate(expiryDate),
        sites: [],
        maxSites: licenseType.maxSites,
        price: isFreeLicense ? 0 : newLicensePrice,
        transactionId: isFreeLicense ? 'FREE-LICENSE' : undefined,
        licenseKey: ''
      };

      await LicenseService.createLicense(newLicense);

      setMessage(`New ${licenseType.name} license created successfully!`);
      setShowCreateLicense(false);
      setNewLicenseType('');
      setNewLicensePrice(0);
      setNewLicenseDuration(12);
      setIsFreeLicense(false);
      setLicenseReason('');
      onLicenseUpdate();
    } catch (error) {
      console.error('Error creating license:', error);
      setMessage('Error creating license');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLicenseKey = async () => {
    if (!selectedLicense) {
      setMessage('Please select a license to generate key for');
      return;
    }

    setLoading(true);
    try {
      const license = licenses.find(l => l.id === selectedLicense);
      if (!license) {
        setMessage('License not found');
        return;
      }

      // Generate a simple license key (in production, use a proper key generation service)
      const licenseKey = `LAVA-${license.licenseType.toUpperCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // In a real app, you'd store this key in Firestore
      setMessage(`License key generated: ${licenseKey}`);
      
      // Copy to clipboard
      navigator.clipboard.writeText(licenseKey);
    } catch (error) {
      console.error('Error generating license key:', error);
      setMessage('Error generating license key');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSupportEmail = async () => {
    if (!selectedUser) {
      setMessage('Please select a user to send email to');
      return;
    }

    setLoading(true);
    try {
      const user = users.find(u => u.id === selectedUser);
      if (!user) {
        setMessage('User not found');
        return;
      }

      // In a real app, you'd integrate with an email service
      const emailSubject = 'Lava Addons Support';
      const emailBody = `Hello ${user.displayName || user.email},\n\nThank you for contacting Lava Addons support. We're here to help!\n\nBest regards,\nLava Addons Team`;
      
      const mailtoLink = `mailto:${user.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.open(mailtoLink);
      
      setMessage('Email client opened with support message');
    } catch (error) {
      console.error('Error sending support email:', error);
      setMessage('Error sending support email');
    } finally {
      setLoading(false);
    }
  };

  const getUserLicenses = (userId: string) => {
    return licenses.filter(license => license.userId === userId);
  };

  const getLicenseUser = (licenseId: string) => {
    const license = licenses.find(l => l.id === licenseId);
    if (!license) return null;
    return users.find(u => u.id === license.userId);
  };

  const handleLicenseTypeChange = (licenseTypeId: string) => {
    setNewLicenseType(licenseTypeId);
    const licenseType = LICENSE_TYPES.find(lt => lt.id === licenseTypeId);
    if (licenseType && !isFreeLicense) {
      setNewLicensePrice(licenseType.price);
    }
  };

  return (
    <div className="space-y-6">
      {/* Create New License */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create New License</h3>
          <button
            onClick={() => setShowCreateLicense(!showCreateLicense)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            {showCreateLicense ? 'Cancel' : 'Create License'}
          </button>
        </div>

        {showCreateLicense && (
          <div className="space-y-4 border-t pt-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select User
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Choose a user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName || user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Type
                </label>
                <select
                  value={newLicenseType}
                  onChange={(e) => handleLicenseTypeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Choose license type...</option>
                  {LICENSE_TYPES.map((licenseType) => (
                    <option key={licenseType.id} value={licenseType.id}>
                      {licenseType.name} - ${licenseType.price}/year
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (months)
                </label>
                <input
                  type="number"
                  value={newLicenseDuration}
                  onChange={(e) => setNewLicenseDuration(parseInt(e.target.value))}
                  min="1"
                  max="60"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price
                </label>
                <input
                  type="number"
                  value={newLicensePrice}
                  onChange={(e) => setNewLicensePrice(parseFloat(e.target.value))}
                  disabled={isFreeLicense}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isFreeLicense}
                    onChange={(e) => {
                      setIsFreeLicense(e.target.checked);
                      if (e.target.checked) {
                        setNewLicensePrice(0);
                      } else {
                        const licenseType = LICENSE_TYPES.find(lt => lt.id === newLicenseType);
                        setNewLicensePrice(licenseType?.price || 0);
                      }
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Free License</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (optional)
              </label>
              <input
                type="text"
                value={licenseReason}
                onChange={(e) => setLicenseReason(e.target.value)}
                placeholder="Developer partnership, Customer compensation, etc."
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <button
              onClick={handleCreateNewLicense}
              disabled={loading || !selectedUser || !newLicenseType}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create License'}
            </button>
          </div>
        )}
      </div>

      {/* License Management */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">License Management</h3>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select License
            </label>
            <select
              value={selectedLicense}
              onChange={(e) => setSelectedLicense(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Choose a license...</option>
              {licenses.map((license) => {
                const user = getLicenseUser(license.id!);
                return (
                  <option key={license.id} value={license.id}>
                    {license.licenseType} - {user?.email} ({license.status})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="space-y-2">
            <button
              onClick={handleExtendLicense}
              disabled={loading || !selectedLicense}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Extending...' : 'Extend License (1 Year)'}
            </button>
            
            <button
              onClick={handleActivateLicense}
              disabled={loading || !selectedLicense}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Activating...' : 'Activate License'}
            </button>
            
            <button
              onClick={handleSuspendLicense}
              disabled={loading || !selectedLicense}
              className="w-full bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Suspending...' : 'Suspend License'}
            </button>
          </div>
        </div>
      </div>

      {/* Site Management */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Site Management</h3>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Domain
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleAddSiteToLicense}
              disabled={loading || !selectedLicense || !domain}
              className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Site to License'}
            </button>
          </div>
        </div>
      </div>

      {/* User Management */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">User Management</h3>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select User
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Choose a user...</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName || user.email} ({getUserLicenses(user.id).length} licenses)
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSendSupportEmail}
              disabled={loading || !selectedUser}
              className="w-full bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-700 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Support Email'}
            </button>
          </div>
        </div>
      </div>

      {/* License Key Generation */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">License Key Generation</h3>
        
        <button
          onClick={handleGenerateLicenseKey}
          disabled={loading || !selectedLicense}
          className="w-full bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate License Key'}
        </button>
      </div>

      {/* Status Messages */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.includes('Error') 
            ? 'bg-red-50 border border-red-200 text-red-700' 
            : 'bg-green-50 border border-green-200 text-green-700'
        }`}>
          {message}
        </div>
      )}
    </div>
  );
} 